import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '../lib/i18n';
export function Hero() {
  const {
    t
  } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  // Significantly reduced overlay opacity to make image very visible
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5], [0, 0]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.4], [0, -60]);
  const platformLogos = [{
    name: 'OnlyFans',
    url: 'https://onlyfans.com/caro_loesel',
    image: 'https://cdn.magicpatterns.com/uploads/i7CCbW1WUeNN8JHuCcZRUK/Onlyfans-PNG-Image.webp',
    color: '#00AFF0'
  }, {
    name: 'WhatsApp',
    url: 'https://www.aloa.me/caroloesel',
    image: 'https://cdn.magicpatterns.com/uploads/9MEJWvFeiuYbsJxgxfbpok/Whatsapp-Logo-klein.png',
    color: '#25D366'
  }, {
    name: 'Maloum',
    url: 'https://app.maloum.com/creator/caro_loesel',
    image: 'https://cdn.magicpatterns.com/uploads/4K8cK5AUkF7arnmUcKdH13/maloum.png',
    color: '#e91e63'
  }, {
    name: 'Brezzels',
    url: 'https://app.brezzels.com/u/ninarose',
    image: 'https://cdn.magicpatterns.com/uploads/2HwssDQEkHuym1nfDXCXVU/brezzels.webp',
    color: '#4ECDC4'
  }];
  return <section ref={containerRef} className="relative min-h-[85vh] md:min-h-screen w-full overflow-hidden flex items-end justify-center" data-id="element-111">
      {/* Parallax Background Image */}
      <motion.div className="absolute inset-0 w-full h-[130%] -top-[15%]" style={{
      y: imageY
    }} data-id="element-112">
        <img src="https://cdn.magicpatterns.com/uploads/m29o3PEKnzEq2MTYgPPrqB/Header.jpg" alt="Caro Loesel" className="w-full h-full object-cover object-top" data-id="element-113" />
      </motion.div>

      {/* Minimal white gradient overlay — removed darkening */}
      <motion.div className="absolute inset-0" style={{
      opacity: overlayOpacity
    }} data-id="element-114" />

      {/* Static base gradient — very subtle at bottom only for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" data-id="element-115" />

      {/* Content */}
      <motion.div className="relative z-10 text-center px-6 pb-24 md:pb-32 w-full max-w-4xl mx-auto" style={{
      opacity: contentOpacity,
      y: contentY
    }} data-id="element-116">
        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-2 tracking-tight drop-shadow-lg" data-id="element-117">
          Caro Loesel
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3,
        ease: 'easeOut'
      }} className="mb-10 font-sans text-white text-sm md:text-base font-bold tracking-[0.15em] uppercase drop-shadow-md" data-id="element-118">
          {t.hero.subtitle}
        </motion.p>

        {/* Promo Banner */}
        <motion.a href="https://onlyfans.com/caro_loesel" target="_blank" rel="noopener noreferrer" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 1
      }} className="inline-block mb-4 px-4 py-1.5 bg-hotpink text-white text-xs md:text-sm font-bold uppercase tracking-wider rounded-full shadow-lg shadow-hotpink/30 animate-pulse" data-id="element-119">
          🔥 50% Rabatt auf den OnlyFans Feed!
        </motion.a>

        {/* Platform Logo Links - Single frosted glass container with pill shape */}
        <div className="flex flex-nowrap justify-around items-center w-[calc(100%+3rem)] -mx-6 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-6 py-3 shadow-lg" data-id="element-120">
          {platformLogos.map((platform, index) => <motion.a key={platform.name} href={platform.url} target="_blank" rel="noopener noreferrer" initial={{
          opacity: 0,
          y: 20,
          scale: 0.8
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.5 + index * 0.1,
          ease: 'backOut'
        }} whileHover={{
          scale: 1.1,
          y: -5
        }} className="group flex flex-col items-center gap-1" data-id="element-121">
              <div className="relative w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center overflow-hidden transition-all duration-300 group-hover:drop-shadow-[0_0_15px_rgba(255,255,255,0.6)]" data-id="element-122">
                <img src={platform.image} alt={platform.name} className="w-full h-full object-contain drop-shadow-md" data-id="element-123" />
              </div>
              <span className="text-[10px] md:text-xs font-sans tracking-wider text-white/90 uppercase font-bold drop-shadow-md group-hover:text-white transition-colors" data-id="element-124">
                {platform.name}
              </span>
            </motion.a>)}
        </div>
      </motion.div>

      {/* Scroll-down indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5,
      duration: 0.8
    }} className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1" data-id="element-125">
        <span className="text-white/60 text-xs font-sans tracking-widest uppercase font-semibold drop-shadow-md" data-id="element-126">
          {t.hero.scroll}
        </span>
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        duration: 1.8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-127">
          <ChevronDown className="w-6 h-6 text-hotpink drop-shadow-md" data-id="element-128" />
        </motion.div>
      </motion.div>
    </section>;
}